import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "../locales/en.json";
import dk from "../locales/dk.json";
import uk from "../locales/uk.json";

const ALIAS = {
  en: "en",
  dk: "dk",
  da: "dk",
  uk: "uk",
  ua: "uk",
};

const LANG_LABELS = {
  en: "EN",
  dk: "DK",
  uk: "UA",
};

const ALL_LANGS = ["en", "dk", "uk"];
const DEBUG = Boolean(import.meta?.env?.DEV);

const HERO_SOCIALS_BY_LANG = {
  dk: ["instagram", "facebook", "whatsapp"],
  uk: ["instagram", "telegram", "viber"],
  en: ["instagram", "facebook", "whatsapp"],
};

const HERO_SOCIAL_LABELS = {
  dk: {
    instagram: "Skriv til mig på Instagram",
    facebook: "Skriv til mig på Facebook",
    whatsapp: "Skriv til mig på WhatsApp",
    telegram: "Skriv til mig på Telegram",
    viber: "Skriv til mig på Viber",
  },
  uk: {
    instagram: "Написати мені в Instagram",
    facebook: "Написати мені у Facebook",
    whatsapp: "Написати мені у WhatsApp",
    telegram: "Написати мені в Telegram",
    viber: "Написати мені у Viber",
  },
  en: {
    instagram: "Message me on Instagram",
    facebook: "Message me on Facebook",
    whatsapp: "Message me on WhatsApp",
    telegram: "Message me on Telegram",
    viber: "Message me on Viber",
  },
};

const HERO_SOCIAL_TITLES = {
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  viber: "Viber",
};

const langMenu = document.querySelector(".lang-menu");
const langSummary = document.querySelector(".selected-lang");
const langButtons = {
  en: document.getElementById("btn-en"),
  dk: document.getElementById("btn-dk"),
  uk: document.getElementById("btn-uk"),
};

i18next
  .use(LanguageDetector)
  .init({
    fallbackLng: "dk",
    debug: DEBUG,
    saveMissing: false,
    parseMissingKeyHandler: () => "",
    resources: {
      en: { translation: en },
      dk: { translation: dk },
      uk: { translation: uk },
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  })
  .then(() => {
    const initial = mapLng(i18next.language);

    updateContent();
    updateSelectedLangUI(initial);
    updateLangOptionsVisible(initial);
    setHtmlLang(i18next.language);
    updateHeroSocials(initial);
    updatePricingOrderLinks(initial);

    i18next.on("languageChanged", (lng) => {
      const mapped = mapLng(lng);

      updateContent();
      updateSelectedLangUI(mapped);
      updateLangOptionsVisible(mapped);
      setHtmlLang(lng);
      updateHeroSocials(mapped);
      updatePricingOrderLinks(mapped);
    });

    bindLanguageEvents();
  });

function mapLng(lng) {
  const base = (lng || "").split("-")[0];
  return ALIAS[lng] || ALIAS[base] || "dk";
}

function updateContent() {
  document.querySelectorAll("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    el.innerHTML = i18next.t(key, { defaultValue: "" });
  });
}

function updateLangOptionsVisible(current) {
  ALL_LANGS.forEach((lang) => {
    const btn = langButtons[lang];
    if (!btn) return;

    const li = btn.closest("li") || btn;
    const isCurrent = lang === current;

    li.hidden = isCurrent;
    btn.setAttribute("aria-hidden", String(isCurrent));
    btn.tabIndex = isCurrent ? -1 : 0;
  });
}

function updateSelectedLangUI(lang) {
  if (!langSummary) return;

  const arrow = langSummary.querySelector("svg");

  langSummary.textContent = "";
  langSummary.append(`${LANG_LABELS[lang] || lang.toUpperCase()} `);

  if (arrow) {
    langSummary.appendChild(arrow);
  }

  ALL_LANGS.forEach((item) => {
    const btn = langButtons[item];
    if (!btn) return;

    const isCurrent = item === lang;
    btn.setAttribute("aria-pressed", String(isCurrent));
    btn.setAttribute("aria-checked", String(isCurrent));
  });
}

function setHtmlLang(lng) {
  const code = mapLng(lng);

  if (code === "dk") {
    document.documentElement.lang = "da-DK";
  } else if (code === "uk") {
    document.documentElement.lang = "uk-UA";
  } else {
    document.documentElement.lang = "en";
  }
}

function updateHeroSocials(lang) {
  const code = mapLng(lang);
  const visibleSocials = HERO_SOCIALS_BY_LANG[code] || HERO_SOCIALS_BY_LANG.dk;
  const labels = HERO_SOCIAL_LABELS[code] || HERO_SOCIAL_LABELS.dk;

  const items = document.querySelectorAll(".hero-social-media-list-item");

  items.forEach((item) => {
    const social = item.dataset.social;
    if (!social) return;

    const isVisible = visibleSocials.includes(social);
    const link = item.querySelector(".hero-social-media-list-link");

    item.hidden = !isVisible;
    item.setAttribute("aria-hidden", String(!isVisible));

    if (!link) return;

    if (isVisible) {
      link.removeAttribute("tabindex");
    } else {
      link.setAttribute("tabindex", "-1");
    }

    if (labels[social]) {
      link.setAttribute("aria-label", labels[social]);
    }

    if (HERO_SOCIAL_TITLES[social]) {
      link.setAttribute("title", HERO_SOCIAL_TITLES[social]);
    }
  });
}

// Зміна посилань на соціальні мережі відповідно до обраної мови 

const PRICING_ORDER_LINKS = {
  dk: {
    href: "https://wa.me/4591827474",
    target: "_blank",
    rel: "noopener noreferrer",
    title: "Bestil via WhatsApp",
    ariaLabel: "Bestil via WhatsApp",
  },
  en: {
    href: "https://wa.me/4591827474",
    target: "_blank",
    rel: "noopener noreferrer",
    title: "Order via WhatsApp",
    ariaLabel: "Order via WhatsApp",
  },
  uk: {
    href: "https://www.instagram.com/_marina__tarot_?igsh=MTVwNmwxN3oxZTZnOA%3D%3D&utm_source=qr",
    target: "_blank",
    rel: "noopener noreferrer",
    title: "Замовити через Instagram",
    ariaLabel: "Замовити через Instagram",
  },
};

function updatePricingOrderLinks(lang) {
  const code = mapLng(lang);
  const config = PRICING_ORDER_LINKS[code] || PRICING_ORDER_LINKS.dk;

  const orderLinks = document.querySelectorAll("[data-order-link]");

  orderLinks.forEach((link) => {
    link.setAttribute("href", config.href);
    link.setAttribute("target", config.target);
    link.setAttribute("rel", config.rel);
    link.setAttribute("title", config.title);
    link.setAttribute("aria-label", config.ariaLabel);
  });
}


function changeLang(code) {
  i18next.changeLanguage(code);
  closeLangMenu();
}

function closeLangMenu() {
  langMenu?.removeAttribute("open");
  langSummary?.blur();
}

function bindLanguageEvents() {
  langButtons.en?.addEventListener("click", () => changeLang("en"));
  langButtons.dk?.addEventListener("click", () => changeLang("dk"));
  langButtons.uk?.addEventListener("click", () => changeLang("uk"));

  document.addEventListener("click", (event) => {
    if (!langMenu) return;

    const isClickInside = langMenu.contains(event.target);
    if (!isClickInside) {
      closeLangMenu();
    }
  });

  document.addEventListener("touchstart", (event) => {
    if (!langMenu) return;

    const isTouchInside = langMenu.contains(event.target);
    if (!isTouchInside) {
      closeLangMenu();
    }
  });
}